import React, { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import usePermission from '@/hooks/usePermission';
import { DashboardContent } from '@/layouts/dashboard';
import { DottedTrail } from '@/pages/dashboard/home/hero-welcome';
import { TiaBanner } from '@/components/custom/tia-banner';
import tiaBoasVindas from '@/assets/images/tia/TIA-BOAS-VINDAS.png';
import { Loader } from '@/components/custom/loader';
import { Page, PageContent, PageSidebar } from '@/components/custom/page';
import { Filter, FilterContent, FilterCombobox } from '@/components/custom/filter';
import { PostShare } from './PostShare';
import { PostCard } from './PostCard';
import { ProfessorsSection } from './ProfessorsSection';

const FEED_WELCOME_MESSAGE = 'Compartilhe conquistas e tire dúvidas com a turma. Aqui a gente cresce junto! 🚀';

const FEED_TIA_DECORATION = (
  <>
    <DottedTrail />
    <span
      aria-hidden="true"
      className="pointer-events-none absolute right-5 top-2 text-xl"
    >
      ⭐
    </span>
    <span
      aria-hidden="true"
      className="pointer-events-none absolute right-12 top-6 text-lg"
      style={{ transform: 'rotate(-20deg)' }}
    >
      ✈️
    </span>
  </>
);

function FeedPlayful() {
  const { t } = useTranslation();
  const client = useClient();

  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [posts, setPosts] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [professors, setProfessors] = useState([]);
  const [isLoadingClassrooms, setIsLoadingClassrooms] = useState(false);
  const [isLoadingProfessors, setIsLoadingProfessors] = useState(false);
  const [selectedClassroomId, setSelectedClassroomId] = useState('blank');

  const getPostsList = async () => {
    setHasMore(true);
    setPageNumber(1);
    const classroomId = selectedClassroomId === 'blank' ? null : selectedClassroomId;
    try {
      const { data } = await client.fetch(`posts?page[number]=1&page[size]=10${classroomId ? `&filter[classroom_id]=${classroomId}` : ''}`);
      if (data.length === 0) {
        setHasMore(false);
      }
      setPosts([...data]);
    } catch (e) {
      toast.error(t('toast.errorGetPost'));
    }
  };

  const updatePostsList = async () => {
    const classroomId = selectedClassroomId === 'blank' ? null : selectedClassroomId;

    try {
      const { data } = await client.fetch(`posts?page[number]=${pageNumber}&page[size]=10${classroomId ? `&filter[classroom_id]=${classroomId}` : ''}`);

      if (data.length === 0) {
        setHasMore(false);
      }

      setPosts(prev => [...prev, ...data]);
    } catch (e) {
      toast.error(t('toast.errorGetPost'));
    }
  };

  const getClassrooms = async () => {
    setIsLoadingClassrooms(true);
    const { error, data } = await client.fetch('classrooms');

    if (error) {
      toast.error(t('toast.errorGetClassroom'));
    } else {
      setClassrooms(data);
    }
    setIsLoadingClassrooms(false);
  };

  const getProfessors = async () => {
    setIsLoadingProfessors(true);
    const { error, data } = await client.fetch('users?filter[profile]=Professor');

    if (error) {
      toast.error(t('toast.errorGetClassroom'));
    } else {
      setProfessors(data);
    }
    setIsLoadingProfessors(false);
  };

  useEffect(() => {
    getClassrooms();
    getProfessors();
  }, []);

  useEffect(() => {
    getPostsList();
  }, [selectedClassroomId]);

  useEffect(() => {
    if (pageNumber !== 1) {
      updatePostsList();
    }
  }, [pageNumber]);

  const addPost = post => setPosts(prev => [post, ...prev]);

  const classroomOptions = React.useMemo(
    () => [
      { value: 'blank', label: 'Todas' },
      ...(classrooms?.map(c => ({
        value: c?.id,
        label: c?.institution?.name + '-' + c?.course?.title + '-' + c?.title
      })) || [])
    ],
    [classrooms]
  );

  const breadcrumbs = {
    title: 'Comunidade',
    nav: [{ route: '/feed', name: 'Fórum', isActive: true }]
  };

  return (
    <DashboardContent breadcrumbs={breadcrumbs}>
      <div className="flex flex-col gap-4">
        <TiaBanner
          image={tiaBoasVindas}
          imageHeight={170}
          imageOffset={200}
          bubbleCorner="tl"
          bubbleMaxWidth={400}
          text={FEED_WELCOME_MESSAGE}
          decoration={FEED_TIA_DECORATION}
          className="sm:mt-8"
        />

        <Page className="gap-y-6 lg:gap-y-[60px]">
          <PageContent>
            <Filter className="mb-0">
              <FilterContent>
                <FilterCombobox
                  label={t('filter.labelSelectTheClassroom')}
                  value={selectedClassroomId}
                  onValueChange={setSelectedClassroomId}
                  options={classroomOptions}
                  loading={isLoadingClassrooms}
                />
              </FilterContent>
            </Filter>

            {usePermission('Criar postagens e comentários', 'Fórum') && (
              <PostShare
                selectedClassroomId={selectedClassroomId}
                onPostCreated={addPost}
              />
            )}

            <InfiniteScroll
              dataLength={posts.length}
              next={() => setPageNumber(pageNumber + 1)}
              hasMore={hasMore}
              loader={<Loader />}
              className="-m-3 flex flex-col gap-4 p-3"
            >
              {posts.map(post => (
                <PostCard
                  key={post.id}
                  post={post}
                  setPosts={setPosts}
                />
              ))}
            </InfiniteScroll>
          </PageContent>

          <PageSidebar forum>
            <ProfessorsSection
              professors={professors}
              loading={isLoadingProfessors}
            />
          </PageSidebar>
        </Page>
      </div>
    </DashboardContent>
  );
}

export default FeedPlayful;
